/**
 * ResearchSessionsDrawer - Slide-over sheet for browsing and starting keyword research.
 *
 * Displays all topical maps (completed + in-progress) as expandable accordion items.
 * Includes a compact seed keyword input to start new research without leaving
 * the Content Planner creation form.
 */

import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { Layers, ChevronDown, ChevronRight, Database, Search, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { contentPlannerService } from '@/services/contentPlannerService';
import { useSeedKeywordAnalysis } from '@/hooks/useContentPlannerAPI';
import { ResearchedKeywordsPanel } from './ResearchedKeywordsPanel';
import { SeedKeywordSuggestions } from './SeedKeywordAnalyzer';
import { ResearchActivityLog } from './ResearchActivityLog';
import type { TopicalMapSummary, SeedKeywordAnalysis, ProcessingLogEntry } from '@/types/contentPlanner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ResearchSessionsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  allMaps: Array<
    Pick<
      TopicalMapSummary,
      | 'map_id'
      | 'seed_keyword'
      | 'total_clusters'
      | 'total_keywords'
      | 'total_volume'
      | 'status'
      | 'progress_percentage'
      | 'current_stage'
      | 'processing_log'
      | 'processing_started_at'
    >
  >;
  tenantId: string;
  onAddKeyword: (keyword: string) => void;
  onAddKeywords: (keywords: string[]) => void;
  currentKeywordsSet: Set<string>;
  refetchMaps: () => void;
  brandBible: {
    business_name?: string;
    services_offered?: string[];
    customer_pain_points?: string;
    marketing_focus?: string;
    ideal_client?: string;
  } | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'research-drawer-width';
const MIN_WIDTH = 400;
const MAX_WIDTH = 900;
const DEFAULT_WIDTH = 600;

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  fetching_keywords: 'Fetching Keywords',
  classifying: 'Classifying Intent',
  clustering: 'Clustering',
  organizing: 'Organizing',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ResearchSessionsDrawer({
  open,
  onOpenChange,
  allMaps,
  tenantId,
  onAddKeyword,
  onAddKeywords,
  currentKeywordsSet,
  refetchMaps,
  brandBible,
}: ResearchSessionsDrawerProps) {
  const [expandedMapId, setExpandedMapId] = useState<string | null>(null);
  const [seedKeyword, setSeedKeyword] = useState('');
  const [isResearching, setIsResearching] = useState(false);

  // ---- Research progress state (SSE-driven) ----
  const [researchProgress, setResearchProgress] = useState<{
    status: string;
    progress: number;
    stage?: string;
  } | null>(null);
  const [processingLogs, setProcessingLogs] = useState<ProcessingLogEntry[]>([]);
  const [processingStartedAt, setProcessingStartedAt] = useState<string | null>(null);
  const [expandedInProgressMapId, setExpandedInProgressMapId] = useState<string | null>(null);

  // ---- AI Suggestions state ----
  const [suggestions, setSuggestions] = useState<SeedKeywordAnalysis[]>([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const analyzeMutation = useSeedKeywordAnalysis();

  // Rotating progress messages while AI suggest is processing
  const [suggestMsgIndex, setSuggestMsgIndex] = useState(0);
  const SUGGEST_MESSAGES = useMemo(() => [
    'Analyzing your brand profile...',
    'Generating keyword suggestions with AI...',
    'Enriching keywords with search volume data...',
    'Computing opportunity scores and rankings...',
    'Finalizing analysis — almost done...',
  ], []);
  useEffect(() => {
    if (!analyzeMutation.isPending) {
      setSuggestMsgIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setSuggestMsgIndex(prev => Math.min(prev + 1, SUGGEST_MESSAGES.length - 1));
    }, 8000);
    return () => clearInterval(interval);
  }, [analyzeMutation.isPending, SUGGEST_MESSAGES]);

  // ---- Resize state ----
  const [width, setWidth] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Number(stored))) : DEFAULT_WIDTH;
  });
  const [isDragging, setIsDragging] = useState(false);
  const widthRef = useRef(width);
  widthRef.current = width;

  // ---- Derived map lists ----
  const completedMaps = useMemo(
    () => allMaps.filter((m) => m.status === 'completed'),
    [allMaps],
  );
  const inProgressMaps = useMemo(
    () => allMaps.filter((m) => !['completed', 'failed'].includes(m.status)),
    [allMaps],
  );

  const totalKeywords = useMemo(
    () => completedMaps.reduce((sum, m) => sum + m.total_keywords, 0),
    [completedMaps],
  );

  // ---- Resize handler ----
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = widthRef.current;
    setIsDragging(true);

    const onMouseMove = (moveEvent: MouseEvent) => {
      const delta = startX - moveEvent.clientX;
      const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startWidth + delta));
      setWidth(newWidth);
    };

    const onMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      localStorage.setItem(STORAGE_KEY, String(widthRef.current));
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  // ---- Research handler ----
  const handleStartResearch = useCallback(() => {
    if (!seedKeyword.trim() || !tenantId) return;
    setIsResearching(true);
    setResearchProgress({ status: 'starting', progress: 0, stage: 'Initializing...' });
    setProcessingLogs([]);
    setProcessingStartedAt(null);

    contentPlannerService.createMapWithStreaming(
      { seed_keyword: seedKeyword.trim(), region: 'US', language: 'en' },
      {
        onCreated: (mapId) => {
          setExpandedInProgressMapId(mapId);
          refetchMaps();
        },
        onProgress: (status, progress, stage) => {
          setResearchProgress({ status, progress, stage });
          refetchMaps();
        },
        onActivityLog: (entries, _total, startedAt) => {
          setProcessingLogs((prev) => [...prev, ...entries]);
          if (startedAt) setProcessingStartedAt(startedAt);
        },
        onComplete: (_mapId, _status, fromCache) => {
          setIsResearching(false);
          setResearchProgress(null);
          setProcessingLogs([]);
          setProcessingStartedAt(null);
          setExpandedInProgressMapId(null);
          setSeedKeyword('');
          refetchMaps();
          toast.success(
            fromCache
              ? 'Found existing research for this keyword!'
              : 'Keyword research complete!',
          );
        },
        onError: (msg) => {
          setIsResearching(false);
          setResearchProgress(null);
          toast.error(`Research failed: ${msg}`);
        },
      },
    );
  }, [seedKeyword, tenantId, refetchMaps]);

  // ---- AI Suggest handler ----
  const handleAISuggest = useCallback(async () => {
    // Pass whatever fields are available — backend fetches full brand bible
    // from DB using tenant_id as fallback when fields are empty.
    try {
      const result = await analyzeMutation.mutateAsync({
        services_offered: brandBible?.services_offered || [],
        customer_pain_points: brandBible?.customer_pain_points || '',
        marketing_focus: brandBible?.marketing_focus || '',
        ideal_client: brandBible?.ideal_client || '',
        business_name: brandBible?.business_name || '',
      });
      setSuggestions(result.suggestions);
      if (result.suggestions.length > 0) {
        setSuggestionsOpen(true);
      } else {
        toast('No keyword suggestions generated. Try updating your Brand Bible with more details.');
      }
    } catch {
      toast.error('Failed to analyze keywords. Please try again.');
    }
  }, [brandBible, analyzeMutation]);

  const toggleExpanded = (mapId: string) => {
    setExpandedMapId((prev) => (prev === mapId ? null : mapId));
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex flex-col p-0"
        style={{ width: `${width}px`, maxWidth: `${width}px` }}
      >
        {/* Resize handle — left edge */}
        <div
          onMouseDown={handleResizeStart}
          className={cn(
            'absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize z-50',
            'hover:bg-teal/30 transition-colors',
            isDragging && 'bg-teal/40',
          )}
        />

        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <SheetTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-teal" />
            Research Sessions
          </SheetTitle>
          <SheetDescription>
            {completedMaps.length} session{completedMaps.length !== 1 ? 's' : ''}{' '}
            &middot; {totalKeywords.toLocaleString()} keywords available
          </SheetDescription>
        </SheetHeader>

        {/* Research input + AI Suggest */}
        <div className="px-4 py-3 border-b">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleStartResearch();
            }}
            className="flex items-center gap-2"
          >
            <Input
              value={seedKeyword}
              onChange={(e) => setSeedKeyword(e.target.value)}
              placeholder="Enter seed keyword..."
              className="h-8 text-sm"
              disabled={isResearching}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={`${analyzeMutation.isPending ? "" : "animate-ai-suggest"} h-8 shrink-0 text-teal border-teal/30 hover:bg-teal-light hover:border-teal/50`}
              onClick={handleAISuggest}
              disabled={analyzeMutation.isPending}
            >
              {analyzeMutation.isPending ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <>
                  <Sparkles className="h-3.5 w-3.5 mr-1" />
                  {suggestions.length > 0 ? 'Refresh' : 'AI Suggest'}
                </>
              )}
            </Button>
            <Button
              type="submit"
              size="sm"
              className="h-8 shrink-0 bg-teal hover:bg-teal-hover text-white"
              disabled={!seedKeyword.trim() || isResearching}
            >
              {isResearching ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <>
                  <Search className="h-3.5 w-3.5 mr-1" />
                  Research
                </>
              )}
            </Button>
          </form>
          {/* AI Suggest progress messages */}
          {analyzeMutation.isPending && (
            <div className="mt-2 flex items-center gap-2 text-xs text-teal-dark">
              <RefreshCw className="h-3 w-3 animate-spin shrink-0" />
              <span className="animate-pulse">{SUGGEST_MESSAGES[suggestMsgIndex]}</span>
            </div>
          )}

          {/* Inline progress bar — only before the in-progress card appears */}
          {isResearching && researchProgress && inProgressMaps.length === 0 && (
            <div className="mt-2 space-y-1">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="truncate">{researchProgress.stage || 'Starting...'}</span>
                <span className="shrink-0 font-medium text-teal-dark">
                  {researchProgress.progress}%
                </span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-teal rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${researchProgress.progress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <ScrollArea className="flex-1">
          {/* AI Suggestions — collapsible table */}
          {suggestions.length > 0 && (
            <div className="px-4 pt-3">
              <SeedKeywordSuggestions
                suggestions={suggestions}
                onSelectKeyword={(kw) => setSeedKeyword(kw)}
                isOpen={suggestionsOpen}
                onOpenChange={setSuggestionsOpen}
              />
            </div>
          )}

          {/* In-progress sessions — clickable with expandable live log */}
          {inProgressMaps.length > 0 && (
            <div className="px-4 pt-3 pb-1 space-y-2">
              {inProgressMaps.map((map) => {
                const isExpanded = expandedInProgressMapId === map.map_id;
                const progress = researchProgress?.progress ?? map.progress_percentage;
                const stage = researchProgress?.stage || STATUS_LABELS[map.status] || map.status;

                return (
                  <div
                    key={map.map_id}
                    className="rounded-lg border border-teal/20 overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedInProgressMapId((prev) =>
                          prev === map.map_id ? null : map.map_id,
                        )
                      }
                      className="w-full bg-teal-light hover:bg-teal/10 transition-colors p-2.5 text-left"
                    >
                      <div className="flex items-center gap-2">
                        <RefreshCw className="h-4 w-4 text-teal animate-spin shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium truncate block">
                            {map.seed_keyword}
                          </span>
                          <span className="text-xs text-muted-foreground truncate block">
                            {stage}
                          </span>
                        </div>
                        <span className="text-xs font-medium text-teal-dark shrink-0">
                          {progress}%
                        </span>
                        {isExpanded ? (
                          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        )}
                      </div>
                      {/* Progress bar strip */}
                      <div className="mt-2 h-1 bg-teal/10 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-teal rounded-full transition-all duration-500 ease-out"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </button>

                    {/* Expanded: live processing log (fall back to API data for orphaned maps) */}
                    {isExpanded && (() => {
                      const liveEntries = processingLogs.length > 0 ? processingLogs : null;
                      const apiEntries = map.processing_log?.filter(Boolean) ?? [];
                      const entries = liveEntries ?? apiEntries;
                      const started = processingStartedAt ?? map.processing_started_at ?? null;
                      return (
                        <div className="border-t border-teal/10 bg-card">
                          <ResearchActivityLog
                            entries={entries}
                            startedAt={started}
                            isProcessing={isResearching}
                          />
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          )}

          {/* Completed sessions */}
          <div className="p-4 space-y-3">
            {completedMaps.map((map) => {
              const isExpanded = expandedMapId === map.map_id;

              return (
                <div
                  key={map.map_id}
                  className="border rounded-lg overflow-hidden"
                >
                  {/* Clickable header */}
                  <button
                    type="button"
                    onClick={() => toggleExpanded(map.map_id)}
                    className="w-full flex items-center justify-between p-3 bg-muted hover:bg-muted transition-colors text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Layers className="h-4 w-4 shrink-0 text-teal" />
                      <span className="font-medium text-sm truncate">
                        {map.seed_keyword}
                      </span>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                        <span>{map.total_clusters} clusters</span>
                        <span>&middot;</span>
                        <span>{map.total_keywords} keywords</span>
                        <span>&middot;</span>
                        <span>
                          {(map.total_volume ?? 0).toLocaleString()} vol
                        </span>
                      </div>
                    </div>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 shrink-0 transition-transform',
                        isExpanded && 'rotate-180',
                      )}
                    />
                  </button>

                  {/* Expanded: cluster table */}
                  {isExpanded && (
                    <div className="p-3 bg-card border-t">
                      <ResearchedKeywordsPanel
                        mapId={map.map_id}
                        tenantId={tenantId}
                        onAddKeyword={onAddKeyword}
                        onAddKeywords={onAddKeywords}
                        currentKeywordsSet={currentKeywordsSet}
                        maxClusters={50}
                      />
                    </div>
                  )}
                </div>
              );
            })}

            {completedMaps.length === 0 && inProgressMaps.length === 0 && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No research sessions yet. Enter a seed keyword above to start.
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
